// /sworn-translator/documents — Wave 65: TH hub of the six certified-translation document families
import { createFileRoute, Link } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/sworn-translator/documents";
const URL = `${SITE}${PATH}`;
const TITLE = "เอกสารที่ต้องแปลและรับรองคำแปล — ครบ 6 กลุ่ม | NYC";
const DESC =
  "รวม 6 กลุ่มเอกสารที่ต้องใช้คำแปลรับรองสำหรับศาล ราชการไทย และหน่วยงานต่างประเทศ พร้อมลำดับขั้นตอน เช็กลิสต์ และคำถามที่พบบ่อย อ้างอิงกรมการกงสุลและกฎหมายวิธีพิจารณาความ";

export const Route = createFileRoute("/sworn-translator/documents/")({
  loader: async () => {
    const { SWORN_DOC_GROUPS } = await import("@/data/sworn-doc-groups");
    return {
      __d: { SWORN_DOC_GROUPS },
      groups: SWORN_DOC_GROUPS.map((g) => ({
        slug: g.slug,
        title: g.titleTh,
        faqs: g.faqsTh.map((f) => ({ q: f.q, a: f.a })),
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(TITLE, PATH, "th") },
      { name: "description", content: ctaDesc(DESC, PATH, "th") },
      { property: "og:title", content: ctaTitle(TITLE, PATH, "th") },
      { property: "og:description", content: ctaDesc(DESC, PATH, "th") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "th_TH" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: URL },
      { rel: "alternate", hrefLang: "en", href: `${SITE}/en/sworn-translator/documents` },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          inLanguage: "th",
          mainEntity: (loaderData?.groups ?? []).flatMap((g) =>
            g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          ),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${URL}#groups`,
          name: "กลุ่มเอกสารที่ต้องแปลและรับรองคำแปล",
          numberOfItems: (loaderData?.groups ?? []).length,
          itemListElement: (loaderData?.groups ?? []).map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.title,
            url: `${URL}/${g.slug}`,
          })),
        }),
      },
    ],
  }),
  component: SwornDocumentsPage,
});

function SwornDocumentsPage() {
  const { __d: { SWORN_DOC_GROUPS } } = Route.useLoaderData() as { __d: typeof import("@/data/sworn-doc-groups") };
  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/sworn-translator" className="hover:underline">
          แปลและรับรองคำแปล
        </Link>{" "}
        / เอกสารที่รับแปลและรับรอง
      </nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
        เอกสารที่ต้องแปลและรับรองคำแปล — ครบ 6 กลุ่ม
      </h1>

      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        คำแปลที่ใช้กับศาล หน่วยงานราชการไทย และหน่วยงานต่างประเทศ ต้องมีคำรับรองความถูกต้องของคำแปลแนบท้าย
        และในหลายกรณีต้องผ่านการรับรองนิติกรณ์ที่กรมการกงสุล กระทรวงการต่างประเทศ ก่อนยื่นสถานทูตปลายทาง
        หน้านี้รวมกลุ่มเอกสารตามลักษณะการใช้งาน พร้อมลำดับขั้นตอนและเช็กลิสต์ของแต่ละกลุ่ม
      </p>

      <ul className="mt-8 grid gap-2 sm:grid-cols-2">
        {SWORN_DOC_GROUPS.map((g) => (
          <li key={g.slug}>
            <Link
              to="/sworn-translator/documents/$group"
              params={{ group: g.slug }}
              className="text-primary hover:underline"
            >
              {g.titleTh}
            </Link>
          </li>
        ))}
      </ul>

      {SWORN_DOC_GROUPS.map((g) => (
        <section key={g.slug} id={g.slug} className="mt-12 scroll-mt-24">
          <h2 className="text-2xl font-semibold">{g.titleTh}</h2>
          <p className="mt-2 text-muted-foreground">{g.summaryTh}</p>
          <div className="mt-4 space-y-4 leading-8">
            {g.bodyTh.slice(0, 2).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <Link
            to="/sworn-translator/documents/$group"
            params={{ group: g.slug }}
            className="mt-3 inline-block text-primary hover:underline"
          >
            อ่านคู่มือ {g.titleTh} ฉบับเต็ม →
          </Link>
        </section>
      ))}
          <ServiceAuthorityBlock topic="sworn" locale="th" />
    </main>
  );
}
